import React, { useState } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Switch,
  message,
  Divider,
  Row,
  Col,
  Select,
  InputNumber,
  Typography,
} from "antd";
import {
  SettingOutlined,
  SecurityScanOutlined,
  GlobalOutlined,
  MailOutlined,
} from "@ant-design/icons";
import apiClient from "../utils/apiClient";

const { Title, Text } = Typography;
const { Option } = Select;

const SuperAdminSettings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSave = async (values) => {
    setLoading(true);
    try {
      await apiClient.put("/superadmin/settings", values);
      message.success("Settings saved successfully");
    } catch (error) {
      message.error("Failed to save settings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Title level={2}>System Settings</Title>

      <Row gutter={16}>
        <Col span={12}>
          <Card
            title={
              <>
                <SettingOutlined /> General Settings
              </>
            }
            style={{ marginBottom: 16 }}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSave}
              initialValues={{
                allowRegistration: true,
                emailVerificationRequired: true,
                maxCompaniesPerUser: 1,
                systemMaintenanceMode: false,
                defaultUserRole: "admin",
                sessionTimeout: 24,
              }}
            >
              <Form.Item
                name="allowRegistration"
                label="Allow New Company Registration"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="emailVerificationRequired"
                label="Require Email Verification"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="maxCompaniesPerUser"
                label="Maximum Companies Per User"
              >
                <InputNumber min={1} max={10} style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item name="defaultUserRole" label="Default User Role">
                <Select>
                  <Option value="admin">Admin</Option>
                  <Option value="user">User</Option>
                  <Option value="viewer">Viewer</Option>
                </Select>
              </Form.Item>

              <Form.Item name="sessionTimeout" label="Session Timeout (hours)">
                <InputNumber min={1} max={168} style={{ width: "100%" }} />
              </Form.Item>

              <Divider />

              <Form.Item
                name="systemMaintenanceMode"
                label="System Maintenance Mode"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  size="large"
                >
                  Save General Settings
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col span={12}>
          <Card
            title={
              <>
                <SecurityScanOutlined /> Security Settings
              </>
            }
            style={{ marginBottom: 16 }}
          >
            <Form
              layout="vertical"
              initialValues={{
                passwordMinLength: 8,
                requireSpecialChars: true,
                requireNumbers: true,
                maxLoginAttempts: 5,
                lockoutDuration: 30,
              }}
            >
              <Form.Item
                name="passwordMinLength"
                label="Minimum Password Length"
              >
                <InputNumber min={6} max={20} style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                name="requireSpecialChars"
                label="Require Special Characters"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                name="requireNumbers"
                label="Require Numbers in Password"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item name="maxLoginAttempts" label="Max Login Attempts">
                <InputNumber min={3} max={10} style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                name="lockoutDuration"
                label="Lockout Duration (minutes)"
              >
                <InputNumber min={5} max={120} style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item>
                <Button type="primary" size="large">
                  Save Security Settings
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Card
            title={
              <>
                <MailOutlined /> Email Settings
              </>
            }
          >
            <Form
              layout="vertical"
              initialValues={{
                smtpHost: "smtp.gmail.com",
                smtpPort: 587,
                smtpSecure: true,
                emailFrom: "noreply@travnok.com",
              }}
            >
              <Form.Item name="smtpHost" label="SMTP Host">
                <Input placeholder="smtp.gmail.com" />
              </Form.Item>

              <Form.Item name="smtpPort" label="SMTP Port">
                <InputNumber min={25} max={587} style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                name="smtpSecure"
                label="Use SSL/TLS"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item name="emailFrom" label="From Email Address">
                <Input placeholder="noreply@travnok.com" />
              </Form.Item>

              <Form.Item>
                <Button type="primary" size="large">
                  Save Email Settings
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col span={12}>
          <Card
            title={
              <>
                <GlobalOutlined /> System Information
              </>
            }
          >
            <div style={{ padding: "16px 0" }}>
              <div style={{ marginBottom: 16 }}>
                <Text strong>System Version:</Text>
                <br />
                <Text>Travel CRM v2.1.0</Text>
              </div>

              <div style={{ marginBottom: 16 }}>
                <Text strong>Database Status:</Text>
                <br />
                <Text style={{ color: "#52c41a" }}>Connected</Text>
              </div>

              <div style={{ marginBottom: 16 }}>
                <Text strong>Last Backup:</Text>
                <br />
                <Text>{new Date().toLocaleDateString()}</Text>
              </div>

              <div style={{ marginBottom: 16 }}>
                <Text strong>Server Uptime:</Text>
                <br />
                <Text>15 days, 8 hours</Text>
              </div>

              <Button type="primary" size="large" style={{ marginTop: 16 }}>
                Generate System Report
              </Button>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SuperAdminSettings;
